// Staff roles (admin-backend feature/staff-roles, migration 0037).
// A staff user may hold several roles; a route is allowed when ANY of them allows it.
// The Staff Panel calls the same /owner/* routes as the Owner Panel with the staff token.

export type StaffRole =
  | 'super_admin'
  | 'finance'
  | 'kyc'
  | 'partner_manager'
  | 'order_ops'
  | 'data_content'
  | 'support';

export const STAFF_ROLES: { code: StaffRole; label: string }[] = [
  { code: 'super_admin', label: 'Super Admin' },
  { code: 'finance', label: 'Finance / Accounts' },
  { code: 'kyc', label: 'KYC / Verification' },
  { code: 'partner_manager', label: 'Partner Manager' },
  { code: 'order_ops', label: 'Order Operations' },
  { code: 'data_content', label: 'Data / Content' },
  { code: 'support', label: 'Customer Support' },
];

export const staffRoleLabel = (code: string): string =>
  STAFF_ROLES.find((r) => r.code === code)?.label || code;

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

const SA = 'super_admin';
const FIN = 'finance';
const KYC = 'kyc';
const PM = 'partner_manager';
const OPS = 'order_ops';
const DATA = 'data_content';
const SUP = 'support';

// Owner only: no staff user (super_admin included)
const OWNER_ONLY: StaffRole[] = [];

// Appendix A: every route and the staff roles that may use it.
// Paths keep the doc's {param} placeholders; any one path segment matches them.
export const ROUTE_ROLES: [HttpMethod, string, StaffRole[]][] = [
  // Clients
  ['GET', '/owner/clients', [SA, FIN, SUP]],
  ['GET', '/owner/clients/{admin_id}', [SA, FIN, SUP]],
  ['GET', '/owner/clients/{admin_id}/trial-usage', [SA, FIN, SUP]],
  ['PATCH', '/owner/clients/{admin_id}/block', [SA, SUP]],
  ['PATCH', '/owner/clients/{admin_id}/unblock', [SA, SUP]],
  ['POST', '/owner/clients/{admin_id}/restore', [SA, SUP]],
  ['DELETE', '/owner/clients/{admin_id}', OWNER_ONLY],
  // Agents
  ['GET', '/owner/agents', [SA, SUP]],
  ['POST', '/owner/agents', [SA, SUP]],
  ['GET', '/owner/agents/{admin_id}', [SA, SUP]],
  ['PATCH', '/owner/agents/{admin_id}/activate', [SA, SUP]],
  ['PATCH', '/owner/agents/{admin_id}/deactivate', [SA, SUP]],
  ['POST', '/owner/agents/{admin_id}/restore', [SA, SUP]],
  ['DELETE', '/owner/agents/{admin_id}', OWNER_ONLY],
  ['GET', '/owner/deleted-accounts', OWNER_ONLY],
  // Subscriptions, payments, invoices, website leads
  ['GET', '/owner/subscriptions', [SA, FIN, SUP]],
  ['GET', '/owner/payments', [SA, FIN, SUP]],
  ['GET', '/owner/invoices', [SA, FIN, SUP]],
  ['GET', '/owner/queries', [SA, SUP]],
  ['GET', '/owner/partner-requests', [SA, PM, SUP]],
  // Trial settings
  ['GET', '/owner/trial-settings', [SA, SUP]],
  ['PUT', '/owner/trial-settings', [SA]],
  // Plans
  ['GET', '/owner/plans', [SA, FIN, PM, SUP]],
  ['GET', '/owner/plans/{plan_id}', [SA, FIN, PM, SUP]],
  ['POST', '/owner/plans', [SA]],
  ['PATCH', '/owner/plans/{plan_id}', [SA]],
  ['PATCH', '/owner/plans/{plan_id}/activate', [SA]],
  ['PATCH', '/owner/plans/{plan_id}/deactivate', [SA]],
  ['DELETE', '/owner/plans/{plan_id}', OWNER_ONLY],
  // Global promo codes
  ['GET', '/owner/promocodes', [SA, FIN, PM, SUP]],
  ['GET', '/owner/promocodes/{promo_id}', [SA, FIN, PM, SUP]],
  ['POST', '/owner/promocodes', [SA, PM]],
  ['PATCH', '/owner/promocodes/{promo_id}', [SA, PM]],
  ['PATCH', '/owner/promocodes/{promo_id}/activate', [SA, PM]],
  ['PATCH', '/owner/promocodes/{promo_id}/deactivate', [SA, PM]],
  ['DELETE', '/owner/promocodes/{promo_id}', OWNER_ONLY],
  // Voter signs
  ['GET', '/owner/voter-signs', [SA, DATA]],
  ['GET', '/owner/voter-signs/{sign_id}', [SA, DATA]],
  ['GET', '/owner/voter-signs/{sign_id}/image', [SA, DATA]],
  ['GET', '/owner/voter-signs/{sign_id}/leaders/{slot}/image', [SA, DATA]],
  ['POST', '/owner/voter-signs', [SA, DATA]],
  ['PATCH', '/owner/voter-signs/{sign_id}', [SA, DATA]],
  ['PUT', '/owner/voter-signs/{sign_id}/leaders/{slot}', [SA, DATA]],
  ['DELETE', '/owner/voter-signs/{sign_id}/leaders/{slot}', OWNER_ONLY],
  ['DELETE', '/owner/voter-signs/{sign_id}', OWNER_ONLY],
  // Polling schedules
  ['GET', '/owner/polling-schedules', [SA, DATA, SUP]],
  ['GET', '/owner/polling-schedules/{schedule_id}', [SA, DATA, SUP]],
  ['POST', '/owner/polling-schedules', [SA, DATA]],
  ['PATCH', '/owner/polling-schedules/{schedule_id}', [SA, DATA]],
  ['DELETE', '/owner/polling-schedules/{schedule_id}', OWNER_ONLY],
  // Upload requests
  ['GET', '/owner/upload-requests', [SA, DATA]],
  ['GET', '/owner/upload-requests/{request_id}', [SA, DATA]],
  ['PATCH', '/owner/upload-requests/{request_id}', [SA, DATA]],
  ['POST', '/owner/upload-requests/{request_id}/upload', [SA, DATA]],
  ['PATCH', '/owner/upload-requests/{request_id}/reject', [SA, DATA]],
  ['PATCH', '/owner/upload-requests/{request_id}/complete', [SA, DATA]],
  // Location master
  ['GET', '/owner/locations/states', [SA, PM, DATA, SUP]],
  ['GET', '/owner/locations/districts', [SA, PM, DATA, SUP]],
  ['GET', '/owner/locations/districts/{district_id}', [SA, PM, DATA, SUP]],
  ['POST', '/owner/locations/districts', [SA, DATA]],
  ['PATCH', '/owner/locations/districts/{district_id}', [SA, DATA]],
  ['DELETE', '/owner/locations/districts/{district_id}', OWNER_ONLY],
  ['GET', '/owner/locations/samitis', [SA, PM, DATA, SUP]],
  ['GET', '/owner/locations/samitis/{samiti_id}', [SA, PM, DATA, SUP]],
  ['POST', '/owner/locations/samitis', [SA, DATA]],
  ['PATCH', '/owner/locations/samitis/{samiti_id}', [SA, DATA]],
  ['DELETE', '/owner/locations/samitis/{samiti_id}', OWNER_ONLY],
  ['GET', '/owner/locations/villages', [SA, PM, DATA, SUP]],
  ['GET', '/owner/locations/villages/{village_id}', [SA, PM, DATA, SUP]],
  ['POST', '/owner/locations/villages', [SA, DATA]],
  ['PATCH', '/owner/locations/villages/{village_id}', [SA, DATA]],
  ['DELETE', '/owner/locations/villages/{village_id}', OWNER_ONLY],
  // Social platforms
  ['GET', '/owner/social-platforms', [SA, PM, DATA]],
  ['POST', '/owner/social-platforms', [SA, DATA]],
  ['PATCH', '/owner/social-platforms/{platform_id}', [SA, DATA]],
  // Announcements
  ['GET', '/owner/announcements', [SA, PM]],
  ['POST', '/owner/announcements', [SA, PM]],
  ['POST', '/owner/announcements/preview', [SA, PM]],
  ['PATCH', '/owner/announcements/{announcement_id}/delete', OWNER_ONLY],
  // Partners
  ['GET', '/owner/partners', [SA, FIN, KYC, PM]],
  ['GET', '/owner/partners/{partner_id}', [SA, FIN, KYC, PM]],
  ['PATCH', '/owner/partners/{partner_id}/{action}', [SA, PM]],
  ['PATCH', '/owner/partners/{partner_id}/channels/{channel}/{action}', [SA, PM]],
  ['POST', '/owner/partners/{partner_id}/restore', [SA, PM]],
  ['DELETE', '/owner/partners/{partner_id}', OWNER_ONLY],
  ['GET', '/owner/partners/{partner_id}/promo-codes', [SA, FIN, PM]],
  ['GET', '/owner/partners/{partner_id}/promo-codes/{code_id}', [SA, FIN, PM]],
  ['POST', '/owner/partners/{partner_id}/promo-codes', [SA, PM]],
  ['GET', '/owner/partners/{partner_id}/sms-notifications', [SA, PM, SUP]],
  ['GET', '/owner/partner-orders', [SA, FIN, PM]],
  ['GET', '/owner/partner-reports/summary', [SA, FIN, PM]],
  ['GET', '/owner/partner-reports/top-partners', [SA, FIN, PM]],
  ['GET', '/owner/exports/{kind}.csv', [SA, FIN]],
  ['GET', '/owner/partner-code-settings', [SA, PM]],
  ['PUT', '/owner/partner-code-settings', [SA]],
  ['GET', '/owner/partner-location-requests', [SA, PM]],
  ['GET', '/owner/partner-location-requests/{request_id}', [SA, PM]],
  ['PATCH', '/owner/partner-location-requests/{request_id}/approve', [SA, PM]],
  ['PATCH', '/owner/partner-location-requests/{request_id}/reject', [SA, PM]],
  // KYC
  ['GET', '/owner/kyc/queue', [SA, KYC]],
  ['GET', '/owner/partners/{partner_id}/kyc', [SA, KYC]],
  ['GET', '/owner/partners/{partner_id}/kyc/documents/{document}/url', [SA, KYC]],
  ['GET', '/owner/partners/{partner_id}/kyc/reveal/{item}', [SA, KYC]],
  ['PATCH', '/owner/partners/{partner_id}/kyc/{item}/verify', [SA, KYC]],
  ['PATCH', '/owner/partners/{partner_id}/kyc/{item}/reject', [SA, KYC]],
  ['GET', '/owner/kyc-settings', [SA, KYC]],
  ['PUT', '/owner/kyc-settings', [SA]],
  // Commissions and payouts
  ['GET', '/owner/commission-settings', [SA, FIN]],
  ['PUT', '/owner/commission-settings', [SA]],
  ['GET', '/owner/commission-rules', [SA, FIN, PM]],
  ['POST', '/owner/commission-rules', [SA]],
  ['POST', '/owner/commission-rules/{rule_id}/deactivate', [SA]],
  ['GET', '/owner/commissions', [SA, FIN]],
  ['GET', '/owner/commissions/summary', [SA, FIN]],
  ['GET', '/owner/commissions/unconfigured', [SA, FIN]],
  ['POST', '/owner/commissions/recalculate', [SA, FIN]],
  ['GET', '/owner/commissions/{entry_id}/holds', [SA, FIN]],
  ['POST', '/owner/commissions/{entry_id}/hold', [SA, FIN]],
  ['POST', '/owner/commissions/{entry_id}/release', [SA, FIN]],
  ['POST', '/owner/partners/{partner_id}/commission-holds/release', [SA, FIN]],
  ['GET', '/owner/payouts', [SA, FIN]],
  ['POST', '/owner/partners/{partner_id}/payouts', [SA, FIN]],
  // Wallets
  ['GET', '/owner/wallets', [SA, FIN]],
  ['GET', '/owner/wallet/summary', [SA, FIN]],
  ['GET', '/owner/wallet/reconciliation', [SA, FIN]],
  ['GET', '/owner/wallet/recharges', [SA, FIN]],
  ['POST', '/owner/wallet/recharges/reconcile', [SA, FIN]],
  ['GET', '/owner/wallet/settings', [SA, FIN]],
  ['PUT', '/owner/wallet/settings', [SA]],
  ['GET', '/owner/partners/{partner_id}/wallet', [SA, FIN, PM]],
  ['GET', '/owner/partners/{partner_id}/wallet/ledger', [SA, FIN]],
  ['POST', '/owner/partners/{partner_id}/wallet/credit', [SA, FIN]],
  ['POST', '/owner/partners/{partner_id}/wallet/debit', [SA, FIN]],
  ['POST', '/owner/partners/{partner_id}/wallet/recharges/{recharge_id}/reconcile', [SA, FIN]],
  // Material
  ['GET', '/owner/material/products', [SA, FIN, PM, OPS]],
  ['PATCH', '/owner/material/products/{code}', [SA]],
  ['GET', '/owner/material/products/{code}/rates', [SA, FIN, PM, OPS]],
  ['POST', '/owner/material/products/{code}/rates', [SA]],
  ['GET', '/owner/material/settings', [SA, FIN, OPS]],
  ['PUT', '/owner/material/settings', [SA]],
  ['GET', '/owner/material/slip-data-settings', [SA, OPS]],
  ['PUT', '/owner/material/slip-data-settings', [SA]],
  ['GET', '/owner/material/slip-data', [SA, OPS]],
  ['GET', '/owner/material/orders', [SA, FIN, PM, OPS]],
  ['GET', '/owner/material/orders/{order_id}', [SA, FIN, PM, OPS]],
  ['GET', '/owner/material/orders/{order_id}/files', [SA, OPS]],
  ['GET', '/owner/material/orders/{order_id}/parts', [SA, OPS]],
  ['POST', '/owner/material/orders/{order_id}/parts/{part_index}/link', [SA, OPS]],
  ['POST', '/owner/material/orders/{order_id}/cancel', [SA, OPS]],
  ['POST', '/owner/material/orders/{order_id}/fail', [SA, OPS]],
  ['POST', '/owner/material/orders/{order_id}/force-cancel', [SA, OPS]],
  ['POST', '/owner/material/orders/{order_id}/force-fail', [SA, OPS]],
  ['POST', '/owner/material/orders/{order_id}/cleanup', OWNER_ONLY],
  ['GET', '/owner/material/jobs/stuck', [SA, OPS]],
  ['GET', '/owner/material/postcards', [SA, OPS]],
  ['GET', '/owner/material/postcards/{postcard_id}', [SA, OPS]],
  ['GET', '/owner/material/reports/summary', [SA, FIN]],
  ['GET', '/owner/material/reports/wallet', [SA, FIN]],
  ['GET', '/owner/material/reports/usage', [SA, FIN, OPS]],
  ['GET', '/owner/material/reports/geography', [SA, FIN]],
  ['GET', '/owner/material/reports/risk', [SA, FIN]],
  ['GET', '/owner/material/exports/orders.csv', [SA, FIN, OPS]],
  ['GET', '/owner/material/exports/wallet-ledger.csv', [SA, FIN]],
  ['GET', '/owner/material/exports/wallet-recharges.csv', [SA, FIN]],
  // Owner only: staff users, activity log, login history, two-factor, owner logout
  ['GET', '/owner/staff/roles', OWNER_ONLY],
  ['GET', '/owner/staff', OWNER_ONLY],
  ['POST', '/owner/staff', OWNER_ONLY],
  ['GET', '/owner/staff/{staff_id}', OWNER_ONLY],
  ['PATCH', '/owner/staff/{staff_id}', OWNER_ONLY],
  ['PATCH', '/owner/staff/{staff_id}/activate', OWNER_ONLY],
  ['PATCH', '/owner/staff/{staff_id}/deactivate', OWNER_ONLY],
  ['POST', '/owner/staff/{staff_id}/reset-password', OWNER_ONLY],
  ['DELETE', '/owner/staff/{staff_id}', OWNER_ONLY],
  ['GET', '/owner/staff/{staff_id}/login-history', OWNER_ONLY],
  ['GET', '/owner/activity-logs', OWNER_ONLY],
  ['GET', '/owner/activity-logs/{log_id}', OWNER_ONLY],
  ['GET', '/owner/login-history', OWNER_ONLY],
  ['POST', '/owner/logout', OWNER_ONLY],
  ['GET', '/owner/security/2fa', OWNER_ONLY],
  ['POST', '/owner/security/2fa/setup', OWNER_ONLY],
  ['POST', '/owner/security/2fa/enable', OWNER_ONLY],
  ['POST', '/owner/security/2fa/disable', OWNER_ONLY],
  ['POST', '/owner/security/2fa/backup-codes', OWNER_ONLY],
];

const toRegExp = (pattern: string) =>
  new RegExp(
    `^${pattern
      .split(/\{[^}]+\}/)
      .map((part) => part.replace(/[.*+?^$()|[\]\\]/g, '\\$&'))
      .join('[^/]+')}$`
  );

const COMPILED = ROUTE_ROLES.map(([method, pattern, roles]) => ({
  method,
  pattern,
  re: toRegExp(pattern),
  roles,
}));

// The roles that may call a route. A literal path wins over a {param} one, so
// /owner/partners/{id}/restore is not taken for /owner/partners/{id}/{action}.
export const rolesForRoute = (method: HttpMethod, path: string): StaffRole[] => {
  const clean = path.split('?')[0];
  const exact = COMPILED.find((r) => r.method === method && r.pattern === clean);
  if (exact) return exact.roles;
  const matches = COMPILED.filter((r) => r.method === method && r.re.test(clean));
  if (!matches.length) return OWNER_ONLY;
  // Fewest placeholders = most specific
  const placeholders = (p: string) => (p.match(/\{/g) || []).length;
  matches.sort((a, b) => placeholders(a.pattern) - placeholders(b.pattern));
  return matches[0].roles;
};

// A route is allowed when ANY of the user's roles allows it. Deletes are never allowed.
export const canUseRoute = (userRoles: string[], method: HttpMethod, path: string): boolean => {
  if (method === 'DELETE') return false;
  const allowed = rolesForRoute(method, path);
  return userRoles.some((r) => (allowed as string[]).includes(r));
};
